import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Lock, Info, Calendar, Phone, AlertCircle } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { cn } from "@/utils/stylesUtils";

export function AppAccessModal({
  open,
  onClose,
  access,
  role,
  title,
  subtitle,
  supportPhone,
  activeRestriction = null,
  districtRestriction = null,
  schoolRestriction = null,
}) {
  const isStudent = role === "student";

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [open, onClose]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open || !access) return null;
  const activeSrc = activeRestriction ?? access;
  const studentStartsAt =
    activeSrc.startsAt && isValid(parseISO(activeSrc.startsAt))
      ? parseISO(activeSrc.startsAt)
      : null;
  const studentResumesAt =
    activeSrc.resumesAt && isValid(parseISO(activeSrc.resumesAt))
      ? parseISO(activeSrc.resumesAt)
      : null;

  const defaultTitle = isStudent
    ? districtRestriction && schoolRestriction
      ? "App temporarily turned OFF for students (2 active restrictions)"
      : "App temporarily turned OFF for students"
    : districtRestriction && schoolRestriction
      ? "App temporarily turned OFF for students"
      : districtRestriction
        ? "App Temporarily Turned OFF for Students District-wide"
        : schoolRestriction
          ? "App Temporarily Turned OFF for Students School-wide"
          : "App temporarily turned OFF for students";

  const defaultSubtitle = isStudent ? (
    <>
      Welcome and great job practicing wellness! Your school leaders have
      temporarily turned off the 1-2-3 Wellness app so it can rest for a bit
      too. However, keep doing a good job of taking care of yourself. Learn more
      about what helps you build wellness by{" "}
      <Link
        to="/resourcestab"
        className="font-bold underline"
        onClick={onClose}
      >
        clicking here
      </Link>
      . Awesome job!
    </>
  ) : (
    <>
      <span>
        Students cannot access wellness practice during this period. You can
        still use all educator features as normal.
      </span>
      <span>
        When turned ON, students can access the app. When turned OFF, students
        cannot access the app.
      </span>
    </>
  );

  const Icon = isStudent ? Lock : Info;
  const iconColor = isStudent ? "text-amber-500" : "text-[#3286DA]";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="access-modal-title"
      className="fixed inset-0 bg-black/60 flex items-end justify-center sm:items-center z-[100]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full rounded-t sm:rounded sm:max-w-md sm:mx-4 border border-[#DDDDDD]">
        <div className="w-10 h-1 bg-gray-200 rounded mx-auto mt-3 sm:hidden" />

        <div className="px-4 pt-4 pb-3 sm:px-6 sm:pt-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Icon className={cn("w-5 h-5 shrink-0", iconColor)} />
              <h2
                id="access-modal-title"
                className="text-[16px] font-bold text-gray-900"
              >
                {title ?? defaultTitle}
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1 rounded hover:bg-gray-100 text-gray-400 transition shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-[16px] opacity-50 mb-4">
            {subtitle ?? defaultSubtitle}
          </p>
          {districtRestriction && (
            <EducatorRestrictionCard
              label="District-wide Pause"
              restriction={districtRestriction}
              accent="amber"
            />
          )}

          {schoolRestriction && (
            <EducatorRestrictionCard
              label="School-wide Pause"
              restriction={schoolRestriction}
              accent="blue"
            />
          )}
          {isStudent && !districtRestriction && !schoolRestriction && (
            <>
              <PauseWindowCard
                startsAt={studentStartsAt}
                resumesAt={studentResumesAt}
              />
              {access.reason && <ReasonCard reason={access.reason} />}
            </>
          )}
          {isStudent && supportPhone && (
            <div className="flex items-center justify-between gap-3 border border-[#DDDDDD] rounded p-3 mb-2">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#3286DA] shrink-0" />
                <div>
                  <p className="text-[16px] font-bold">
                    Need to speak to someone?
                  </p>
                  <p className="text-[14px] opacity-50">
                    Support is available during this pause
                  </p>
                </div>
              </div>
              <a
                href={`tel:${supportPhone.replace(/\D/g, "")}`}
                className="shrink-0 text-[16px] font-bold text-[#3286DA] hover:underline"
              >
                {supportPhone}
              </a>
            </div>
          )}
        </div>
        <div className="border-t border-[#DDDDDD] px-4 py-3 sm:px-6">
          <button
            onClick={onClose}
            className="w-full py-2 bg-[#3386D912] border border-[#3286DA] rounded text-[16px] font-bold text-[#1a5fa8] hover:bg-[#3386D924] transition"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function PauseWindowCard({ startsAt, resumesAt }) {
  return (
    <div className="border border-[#DDDDDD] rounded p-3 flex flex-col gap-2 mb-3">
      <div className="flex items-center gap-1.5 text-[14px] font-bold text-gray-400 uppercase tracking-wider">
        <Calendar className="w-3.5 h-3.5" />
        Pause Window
      </div>
      <div className="grid grid-cols-2 gap-3">
        <DateCell label="From" date={startsAt} />
        <DateCell label="Until" date={resumesAt} />
      </div>
    </div>
  );
}

function DateCell({ label, date }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[16px] font-bold">{label}</span>
      <span className="text-[16px]">
        {date ? format(date, "MMM d, yyyy") : "—"}
      </span>
      {date && (
        <span className="text-[14px] opacity-50">{format(date, "h:mm a")}</span>
      )}
    </div>
  );
}

function ReasonCard({ reason }) {
  return (
    <div className="flex gap-2 border border-[#DDDDDD] rounded p-3 mb-3 bg-amber-50">
      <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
      <div className="flex flex-col gap-0.5">
        <span className="text-[16px] font-bold text-amber-700">Reason</span>
        <span className="text-[16px] text-amber-800">{reason}</span>
      </div>
    </div>
  );
}

function EducatorRestrictionCard({ label, restriction, accent }) {
  const startsAt =
    restriction.startsAt && isValid(parseISO(restriction.startsAt))
      ? parseISO(restriction.startsAt)
      : null;
  const resumesAt =
    restriction.resumesAt && isValid(parseISO(restriction.resumesAt))
      ? parseISO(restriction.resumesAt)
      : null;

  const isAmber = accent === "amber";
  const bg = isAmber ? "bg-amber-50" : "bg-blue-50";
  const labelColor = isAmber ? "text-amber-700" : "text-[#1a5fa8]";
  const valueColor = isAmber ? "text-amber-900" : "text-blue-900";

  return (
    <div
      className={cn(
        "border border-[#DDDDDD] rounded p-3 flex flex-col gap-2 mb-3",
        bg,
      )}
    >
      <div
        className={cn(
          "flex items-center gap-1.5 text-[14px] font-bold uppercase tracking-wider",
          labelColor,
        )}
      >
        <Calendar className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-0.5">
          <span className={cn("text-[16px] font-bold", labelColor)}>From</span>
          <span className={cn("text-[16px]", valueColor)}>
            {startsAt ? format(startsAt, "MMM d, yyyy") : "—"}
          </span>
          {startsAt && (
            <span className={cn("text-[14px] opacity-60", labelColor)}>
              {format(startsAt, "h:mm a")}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-0.5">
          <span className={cn("text-[16px] font-bold", labelColor)}>Until</span>
          <span className={cn("text-[16px]", valueColor)}>
            {resumesAt ? format(resumesAt, "MMM d, yyyy") : "—"}
          </span>
          {resumesAt && (
            <span className={cn("text-[14px] opacity-60", labelColor)}>
              {format(resumesAt, "h:mm a")}
            </span>
          )}
        </div>
      </div>
      {restriction.reason && (
        <>
          <div className="h-px bg-[#DDDDDD]" />
          <div className="flex flex-col gap-0.5">
            <span className={cn("text-[16px] font-bold", labelColor)}>
              Reason
            </span>
            <span className={cn("text-[16px]", valueColor)}>
              {restriction.reason}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
